import { useIsAdmin } from "component/admin";
import { Card, HorizBox, Pad, PadBox, Separator, AdaptiveWidth } from "component/basics";
import { ConversationScreen } from "component/screen";
import { CTAButton, TextButton } from "component/button";
import { Heading, UtilityText, DataVizText } from "component/text";
import { useCollection, useDatastore, useObject, useDerivedObject } from "util/datastore";
import { relatedConversationsTable } from "feature/topic/question-cards";
import { useGlobalQuestions } from "component/question";
import { useState, useRef } from "react";
import { Toggle } from "component/form";
import { Catcher } from "system/catcher";
import { spacings } from "component/constants";
import { t, declareDerivedTable, makeTestID } from "util/stdtypes";
import { useConfig } from "util/features";
import { QuestionConfig } from "structure/question";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { ChevronLeft, ChevronRight, Add as AddIcon } from "@carbon/icons-react";
import { colorBorderPrimary, colorSurfaceSecondary, colorTealBackground, colorTextOnDarkBackground, colorTextPrimary, colorTextSecondary } from "component/color";
import { logEventAsync } from "util/eventlog";
import { CommentPreview } from "feature/teaser/editorial-question";
import { dropKey } from "util/util";


export const QuestionRelatedConversationsFeature = {
    key: 'questionrelatedconversations',
    name: 'Related Conversations',
    subscreens: {
        relatedConversationsAdmin: () => <RelatedConversationsAdmin />,
    },
    providesConfig: {
        relatedConversationsTitle: t('Related conversations'),
        bottomRecircWidgets: [RelatedConversations],
    }
}

export type CommentPreviewData = {
    text: string;
    time: number;
    from: string;
    fromName: string | null;
    fromPhoto: string | null;
    fromHue: number | null;
};

export type RelatedQuestionPreview = {
    commentCount: number;
    time: number;
    commentPreview: CommentPreviewData | null;
    participantCount: number;
};

export const relatedQuestionPreviewTable = declareDerivedTable<RelatedQuestionPreview>('relatedQuestionPreview');

export function RelatedConversations() {
    const relatedConversations = useCollection(relatedConversationsTable, { filter: { deleted: false } });
    const [narrow, setNarrow] = useState(false);
    const {relatedConversationsTitle} = useConfig<QuestionConfig>();
    const isAdmin = useIsAdmin();

    return (
        <PadBox vert={spacings.xl2} horiz={spacings.lg}>
            {( !!relatedConversations?.length || isAdmin ) && (
                <AdaptiveWidth setNarrow={setNarrow} breakpoint={400}>
                    <Heading type="large" weight="medium" label={relatedConversationsTitle} />
                    <Pad size={spacings.lg} />
                    <RecirculationSlider cardsData={relatedConversations} narrow={narrow} controls />
                </AdaptiveWidth>
            )}
        </PadBox>
    )
};

export function RelatedConversation({ question }) {
    const datastore = useDatastore();
    const selectedQuestion = useObject(relatedConversationsTable, question.key);
    const isChosen = !!selectedQuestion && !selectedQuestion.deleted;

    async function onChange(selected) {
        await datastore.callServerAsync('questions', selected ? 'selectRelatedQuestion' : 'unselectRelatedQuestion', {questionKey: question.key });
    }

    return <PadBox bottom={20} horiz={20}>
        <Card>
            <HorizBox spread>
                <Toggle text={question.text ?? question.question} value={isChosen} onChange={onChange} />
            </HorizBox>
        </Card>
    </PadBox>
}


export function RelatedConversationsAdmin() {
    const questions = useGlobalQuestions();
    const [search, setSearch] = useState('');
    const filteredQuestions = questions?.filter(question => !search || question.question?.toLowerCase().includes(search.toLowerCase()));

    return (
        <ConversationScreen>
            <Pad />
            <PadBox horiz={20}><Heading label={t('Choose related conversations')} /></PadBox>
            <Pad />
            <Separator />
            <Pad />
            {/* TODO need a new "Question component?" */}
            {filteredQuestions?.map(question =>
                <Catcher key={question.key}><RelatedConversation question={question} /></Catcher>
            )}
        </ConversationScreen>
    )
}

export function ConversationCard({
    text,
    singleCard = false,
    id,
    narrow = false,
}) {
    const questionPreview = useDerivedObject(relatedQuestionPreviewTable, id);
    const participantCount = questionPreview?.participantCount || 0;
    const formatParams = {count: participantCount, singular: t('participant'), plural: t('participants')};
    const datastore = useDatastore();
    const s = createCardStyle({singleCard, narrow});
    
    return (
        <Pressable testID={`conversation-card-${id}`} style={s.card} onPress={() => {
            logEventAsync(datastore, 'related-conversation-click', {structureKey: 'question', instanceKey: id});
            datastore.gotoInstance({ structureKey: 'question', instanceKey: id });
        }}>
            <View style={{flexShrink: 1}}>
                {narrow ? (
                    <DataVizText type="large" color={colorTextOnDarkBackground} text={text} />
                ):(
                    <Heading type="large" weight="medium" color={colorTextOnDarkBackground}  text={text}/>
                )}
                <Pad size={spacings.xs} />
                <UtilityText label={t('{count} {noun}')} formatParams={formatParams} type='tiny' color={colorTextOnDarkBackground} />
            </View>
            <View style={s.spacer}>
                <CommentPreview commentPreview={questionPreview?.commentPreview ?? null} />
            </View>
        </Pressable>
    )
}

const createCardStyle = ({ singleCard, narrow }) =>
    StyleSheet.create({
        card: {
            flexDirection: (singleCard && !narrow) ? 'row' : 'column',
            justifyContent: (singleCard && !narrow) ? 'space-between' : 'flex-start',
            width: (singleCard && !narrow) ? '100%' : narrow ? 285 : 335,
            padding: spacings.lg,
            backgroundColor: colorTealBackground,
            borderRadius: 12,
        },
        spacer: {
            paddingTop: (singleCard && !narrow) ? 0 : spacings.lg,
            flexShrink: 1,
        }
    })

function handleScrollTo({
    scrollRef,
    scrollViewMesurements,
    increment = false,
}) {
    const {x, width, cardWidth} = scrollViewMesurements;
    const position = increment ? Math.max(width, x + cardWidth) : Math.max(0, x - cardWidth);
    scrollRef.scrollTo({x: position, y: 0, animated: true})
}

function SelectConversationCard() {
    const datastore = useDatastore();
    const s = SelectConversationCardStyle;
    const label = t('Select conversation');
    return (
        <Pressable style={s.container} onPress={datastore.needsLogin(() => datastore.pushSubscreen('relatedConversationsAdmin', {}))}>
            <AddIcon color={colorTextSecondary} />
            <Pad size={10} />
            <UtilityText label={label} weight='medium' testID={makeTestID({label})} color={colorTextSecondary} />
        </Pressable>
    )
}

const SelectConversationCardStyle = StyleSheet.create({
    container: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: colorBorderPrimary,
        borderRadius: 10,
        width: 335,
        minHeight: 200,
        backgroundColor: colorSurfaceSecondary,
    }
})

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function RecirculationSlider({
    cardsData,
    controls = false,
    narrow = false,
}) {
    const scrollRef = useRef(null)
    const scrollViewMesurements = useRef({ x: 0, width: 0, cardWidth: 0});
    const s = recirculationSliderStyle;
    const isAdmin = useIsAdmin();

    const cardsStack = cardsData.map((cardData) => {
        return <ConversationCard key={`ConversationCard-${cardData.id}`} singleCard={cardsData.length === 1 && !isAdmin} narrow={narrow} {...dropKey(cardData)} />
    })
    if (isAdmin) {
        cardsStack.unshift(
            <SelectConversationCard />
        )
    }
    
    if(cardsStack === undefined || cardsStack?.length === 0) {
        return <></>;
    }
    
    if (cardsStack?.length === 1) {
        return (
            cardsStack[0]
        )
    }

    return (
        <>
            <ScrollView ref={scrollRef} horizontal showsHorizontalScrollIndicator={false}
                onScroll={
                    ({nativeEvent}) => {
                        scrollViewMesurements.current = {...scrollViewMesurements.current, x: nativeEvent.contentOffset.x}
                    }
                }
                scrollEventThrottle={16}
                onLayout={
                    (event) => {
                        const { width } = event.nativeEvent.layout;
                        scrollViewMesurements.current = {...scrollViewMesurements.current, width, cardWidth: Math.round(width / cardsData.length)}
                    }
                }>
                {cardsStack.map((card, index) =>
                    <View key={`ConversationCardView-${index}`} style={s.container}>
                        {card}
                        {cardsStack?.length !== index + 1 && (
                            <Pad size={spacings.lg} />
                        )}
                    </View>
                )}
            </ScrollView>
            {controls && (
                <>
                    <Pad size={spacings.lg} />
                    <HorizBox spread center>
                        <TextButton label={t('Previous')} leftIcon={ChevronLeft} color={colorTextSecondary} onPress={() => handleScrollTo({scrollRef: scrollRef.current!, scrollViewMesurements: scrollViewMesurements.current})} />
                        <TextButton label={t('Next')} rightIcon={ChevronRight} color={colorTextSecondary} onPress={() => handleScrollTo({scrollRef: scrollRef.current!, scrollViewMesurements: scrollViewMesurements.current, increment: true})} />
                    </HorizBox>
                </>
            )}

        </>
    )
}

const recirculationSliderStyle = StyleSheet.create({
    container: {
        flexDirection: 'row',
    }
})

